<template>
    <div class="member-pagination-wrapper" :class="{ 'is-extranet': isExtranetContext }">
        <nav class="member-pagination" aria-label="Pagination">
            <ul class="pagination mb-0 align-items-center" style="gap: 4px; border-radius: 0;">
                
                <!-- First Page Button -->
                <li class="page-item" :class="{ disabled: sPage <= 1 }">
                    <button 
                        type="button"
                        class="pagination-btn pagination-btn--edge page-link"
                        :disabled="sPage <= 1"
                        @click="handleClick(1, $event)"
                        aria-label="Go to first page"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="11 17 6 12 11 7"></polyline>
                            <polyline points="18 17 13 12 18 7"></polyline>
                        </svg>
                    </button>
                </li>

                <!-- Previous Page Button -->
                <li class="page-item" :class="{ disabled: sPage <= 1 }">
                    <button 
                        type="button"
                        class="pagination-btn pagination-btn--nav page-link"
                        :disabled="sPage <= 1"
                        @click="handleClick(sPage - 1, $event)"
                        aria-label="Go to previous page"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                </li>

                <!-- Page Numbers -->
                <li
                    v-for="item in renderList"
                    :key="item"
                    class="page-item"
                    :class="{ active: (sPage == item) }"
                >
                    <button
                        type="button"
                        class="pagination-btn pagination-btn--page page-link"
                        @click="handleClick(item, $event)"
                    >
                        {{ item }}
                    </button>
                </li>

                <!-- Next Page Button -->
                <li class="page-item" :class="{ disabled: sPage >= totalPages }">
                    <button 
                        type="button"
                        class="pagination-btn pagination-btn--nav page-link"
                        :disabled="sPage >= totalPages"
                        @click="handleClick(sPage + 1, $event)"
                        aria-label="Go to next page"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </li>

                <!-- Last Page Button -->
                <li class="page-item" :class="{ disabled: sPage >= totalPages }">
                    <button 
                        type="button"
                        class="pagination-btn pagination-btn--edge page-link"
                        :disabled="sPage >= totalPages"
                        @click="handleClick(totalPages, $event)"
                        aria-label="Go to last page"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="13 17 18 12 13 7"></polyline>
                            <polyline points="6 17 11 12 6 7"></polyline>
                        </svg>
                    </button>
                </li>

            </ul>
        </nav>
    </div>
</template>

<script>
import isExtranetHostname from '@/helpers/isExtranet'

export default {
    name: 'OPagination',
    props: ['page', 'total', 'searchOptions'],
    data: () => {
        return {
            sPage: 1,
        };
    },
    watch: {
        searchOptions() {
            this.sPage = 1;
            this.$emit('callback', 1);
        },
        page: {
            handler(newPage) {
                let pageNumber = newPage;
                if (typeof newPage === 'string') {
                    pageNumber = parseInt(newPage, 10);
                }
                this.sPage = pageNumber;
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        handleClick(page, event) {
            if (event) event.preventDefault();
            this.sPage = page;
            this.$emit('callback', page);
        },
        goTo(event) {
            if (event) event.preventDefault();
            const nav = event.target.parentNode.dataset.nav;
            this.sPage = parseInt(nav);
        }
    },
    computed: {
        renderList() {
            const pages = [];
            const range = 5;
            const lim = this.total;

            for (let i = 1; i <= lim; i++) {
                pages.push(i);
            }            

            if (pages.length > range) {
                const halfRange = Math.floor(range / 2);
                let start = this.sPage - halfRange - 1;
                let end = this.sPage + halfRange;

                // Ensure start is not negative
                if (start < 0) {
                    start = 0;
                    end = Math.min(range, pages.length);
                } 
                // Ensure end doesn't exceed total pages
                else if (end > pages.length) {
                    end = pages.length;
                    start = Math.max(0, pages.length - range);
                }

                return pages.slice(start, end);
            }
            
            return pages;
        },
        totalPages() {
            return this.total;
        },
        isExtranetContext() {
            // Check if we are running under extranet subdomain or extranet router path
            return isExtranetHostname() || (window.location.pathname || '').startsWith('/extranet');
        }
    },
    mounted() {
        const pageNumber = typeof this.page === 'string' ? parseInt(this.page, 10) : this.page;
        this.sPage = pageNumber;
    },
    updated() {
        // Memastikan sPage selalu sinkron dengan prop page
        const pageProp = typeof this.page === 'string' ? parseInt(this.page, 10) : this.page;
        
        if (this.sPage !== pageProp) {
            this.sPage = pageProp;
        }
    }
};
</script>

<style scoped>
.member-pagination-wrapper {
    display: flex;
    justify-content: center;
    padding: 12px 0;
    margin-top: auto; 
    flex-shrink: 0;
}

.member-pagination {
    display: flex;
    align-items: center;
    background: white;
    padding: 8px 12px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.member-pagination .pagination {
    margin: 0;
}

/* Force override bootstrap's .page-link */
.member-pagination .page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none !important;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #4B5563;
    font-weight: 500;
    margin: 0 !important;
    box-shadow: none !important;
}

.member-pagination .page-link:focus {
    outline: none;
    box-shadow: none !important;
}

/* Edge buttons (first/last) */
.pagination-btn--edge {
    width: 32px;
    height: 32px;
    border-radius: 8px !important;
    padding: 0 !important;
}

.page-item:not(.disabled) .pagination-btn--edge:hover {
    background: #F3F4F6 !important;
}

/* Navigation buttons (prev/next) */
.pagination-btn--nav {
    width: 36px;
    height: 36px;
    border-radius: 8px !important;
    padding: 0 !important;
}

.page-item:not(.disabled) .pagination-btn--nav:hover {
    background: #F3F4F6 !important;
}

/* Page number buttons */
.pagination-btn--page {
    min-width: 36px;
    height: 36px;
    padding: 0 10px !important;
    border-radius: 8px !important;
    font-size: 14px;
}

.page-item:not(.active) .pagination-btn--page:hover {
    background: #F3F4F6 !important;
}

/* Disabled state */
.page-item.disabled .page-link {
    opacity: 0.4;
    cursor: not-allowed;
    background: transparent !important;
    color: #4B5563 !important;
}

/* Active Overrides for extranet ONLY.
   If outside extranet, it relies on global theme to color .page-item.active .page-link 
   but we just need to make sure the border radius matches well. */
.member-pagination-wrapper:not(.is-extranet) .page-item.active .page-link {
    background-color: var(--primary-color, #F27178) !important;
    border-color: var(--primary-color, #F27178) !important;
    color: white !important;
    border-radius: 8px !important;
}
.member-pagination-wrapper:not(.is-extranet) .page-item.active .page-link:hover {
    filter: brightness(0.9);
}

.is-extranet .page-item.active .page-link {
    background-color: var(--extranet-primary, #fc6666) !important;
    border-color: var(--extranet-primary, #fc6666) !important;
    color: white !important;
    border-radius: 8px !important;
}
.is-extranet .page-item.active .page-link:hover {
    filter: brightness(0.9);
}

@media (max-width: 900px) {
    .member-pagination {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    }
    .pagination-btn--edge {
        width: 28px;
        height: 28px;
    }
    .pagination-btn--nav {
        width: 32px;
        height: 32px;
    }
    .pagination-btn--page {
        min-width: 32px;
        height: 32px;
        font-size: 13px;
    }
}
</style>
